import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Official rules for the monthly rewards (App Store guideline 5.3 / Google Play real-money contest rules).
// Keep in sync with backend/rewards_service.py (PRIZE_DISTRIBUTION, MIN_DAILY_CHALLENGES, MIN_SUCCESS_RATE, MIN_PAYOUT_AMOUNT).
const PRIZES = [
  { en: '1st place', sk: '1. miesto', eur: 100 },
  { en: '2nd place', sk: '2. miesto', eur: 50 },
  { en: '3rd place', sk: '3. miesto', eur: 30 },
  { en: '4th place', sk: '4. miesto', eur: 25 },
  { en: '5th to 10th place (each)', sk: '5. až 10. miesto (každé)', eur: 10 },
  { en: '11th to 50th place (each)', sk: '11. až 50. miesto (každé)', eur: 2 },
  { en: '51st to 100th place (each)', sk: '51. až 100. miesto (každé)', eur: 1 },
];

const CONTENT = {
  en: {
    back: 'Back',
    title: 'Official Rules: SocraQuest Monthly Rewards',
    updated: 'Last updated: November 1, 2026',
    place: 'Place',
    prize: 'Prize',
    eur: (n) => `€${n}`,
    sections: [
      ['1. Sponsor', ['The SocraQuest Monthly Rewards program (the "Program") is sponsored and run by Pytech Digital Private Limited (the "Sponsor"), the developer of the SocraQuest app. Contact: support@socraquest.sk.']],
      ['2. Apple and Google are not involved', ['Apple Inc. and Google LLC are not sponsors of the Program and are not involved in it in any manner. Questions, comments or complaints about the Program must be sent to the Sponsor, not to Apple or Google.']],
      ['3. Free to enter', ['No purchase or payment is necessary to take part or to win. SocraQuest has no paid features, and nothing you buy can improve your chances.']],
      ['4. Who can take part', ['Anyone with a SocraQuest account in good standing who is allowed to take part in skill-based contests under the laws where they live. The Program is void where prohibited by law. Participants under 18 need the consent of a parent or legal guardian to receive a prize.']],
      ['5. Contest period', ['Each calendar month is a separate contest period. Days are counted by the server date (UTC).']],
      ['6. How to qualify for a prize', ['In a given month you must:'], ['complete assessments on at least 20 different days, and', 'reach an average score of at least 70% (for each assessment, your best attempt counts).'], ['Inviting other people is not required.']],
      ['7. How winners are determined', ['Participants are ranked by their total number of correct answers in the month, counting the best attempt at each assessment. Ties are broken by the lower total time. Prizes are awarded in this ranking order to participants who meet the requirements in section 6. The result depends only on knowledge and speed, not on chance.']],
      ['8. Prizes (every month)', null, null, null, 'PRIZES', ['Maximum total prizes per month: €395.']],
      ['9. Receiving prizes', ['Prizes are added to your SocraQuest rewards balance after the end of the month. When your balance reaches at least €20, you can request a payout by bank transfer (IBAN) on the Rewards page. A smaller balance carries over to the following months. The Sponsor checks every payout request before paying it and may ask you to confirm your identity. You are responsible for any taxes on prizes.']],
      ['10. Fair play', ['Accounts that cheat, use more than one account, automate answers or otherwise abuse the Program are disqualified and may be closed. Prizes obtained this way are forfeited.']],
      ['11. Deleting your account', ['If you delete your account, unpaid rewards and open payout requests are cancelled.']],
      ['12. Changes', ['The Sponsor may change or end the Program at any time. Changes are published on this page.']],
      ['13. Personal data', ['Personal data is processed according to our '], null, null, null, null, 'PRIVACY'],
    ],
    privacyLink: 'Privacy Policy',
  },
  sk: {
    back: 'Späť',
    title: 'Oficiálne pravidlá: mesačné odmeny SocraQuest',
    updated: 'Posledná aktualizácia: 1. novembra 2026',
    place: 'Miesto',
    prize: 'Výhra',
    eur: (n) => `${n} €`,
    sections: [
      ['1. Organizátor', ['Program mesačných odmien SocraQuest („Program“) organizuje a financuje spoločnosť Pytech Digital Private Limited („Organizátor“), vývojár aplikácie SocraQuest. Kontakt: support@socraquest.sk.']],
      ['2. Apple a Google nie sú zapojení', ['Spoločnosti Apple Inc. a Google LLC nie sú organizátormi ani sponzormi Programu a nijako sa na ňom nepodieľajú. Otázky, pripomienky a sťažnosti k Programu posielaj Organizátorovi, nie spoločnosti Apple ani Google.']],
      ['3. Účasť je zadarmo', ['Na účasť ani na výhru nie je potrebný žiadny nákup ani platba. SocraQuest nemá žiadne platené funkcie a žiadnym nákupom si nezvýšiš šance.']],
      ['4. Kto sa môže zapojiť', ['Každý, kto má platný účet SocraQuest a podľa zákonov krajiny, v ktorej žije, sa smie zúčastňovať vedomostných súťaží. Program neplatí tam, kde to zákon zakazuje. Účastníci mladší ako 18 rokov potrebujú na prevzatie výhry súhlas rodiča alebo zákonného zástupcu.']],
      ['5. Súťažné obdobie', ['Každý kalendárny mesiac je samostatné súťažné obdobie. Dni sa počítajú podľa dátumu servera (UTC).']],
      ['6. Podmienky nároku na výhru', ['V danom mesiaci musíš:'], ['dokončiť hodnotenia aspoň v 20 rôznych dňoch a', 'dosiahnuť priemerné skóre aspoň 70 % (pri každom hodnotení sa počíta tvoj najlepší pokus).'], ['Pozývanie ďalších ľudí nie je podmienkou.']],
      ['7. Určenie víťazov', ['Účastníci sa zoraďujú podľa celkového počtu správnych odpovedí v mesiaci, pričom pri každom hodnotení sa počíta najlepší pokus. Pri rovnosti rozhoduje kratší celkový čas. Výhry sa udeľujú v tomto poradí účastníkom, ktorí splnili podmienky z bodu 6. Výsledok závisí len od vedomostí a rýchlosti, nie od náhody.']],
      ['8. Výhry (každý mesiac)', null, null, null, 'PRIZES', ['Spolu najviac 395 € mesačne.']],
      ['9. Vyplatenie výhier', ['Výhry sa po skončení mesiaca pripíšu na tvoj zostatok odmien v SocraQuest. Keď zostatok dosiahne aspoň 20 €, môžeš na stránke Odmeny požiadať o výplatu bankovým prevodom (IBAN). Nižší zostatok sa prenáša do ďalších mesiacov. Organizátor každú žiadosť pred vyplatením skontroluje a môže ťa požiadať o overenie totožnosti. Prípadné dane z výhier platíš sám.']],
      ['10. Férová hra', ['Účty, ktoré podvádzajú, používajú viac ako jeden účet, automatizujú odpovede alebo inak zneužívajú Program, budú vylúčené a môžu byť zrušené. Takto získané výhry prepadajú.']],
      ['11. Zrušenie účtu', ['Ak svoj účet zrušíš, nevyplatené odmeny a otvorené žiadosti o výplatu prepadajú.']],
      ['12. Zmeny', ['Organizátor môže Program kedykoľvek zmeniť alebo ukončiť. Zmeny zverejní na tejto stránke.']],
      ['13. Osobné údaje', ['Osobné údaje spracúvame podľa dokumentu '], null, null, null, null, 'PRIVACY'],
    ],
    privacyLink: 'Zásady ochrany osobných údajov',
  },
};

export default function RewardsRulesPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').toLowerCase().startsWith('sk') ? 'sk' : 'en';
  const c = CONTENT[lang];

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button onClick={goBack} className="flex items-center text-teal-600 hover:text-teal-700 mr-4">
            <ArrowLeft className="w-5 h-5 mr-1" />
            {c.back}
          </button>
          <h1 className="text-xl font-bold text-slate-800">SocraQuest</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="rewards-rules">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">{c.title}</h1>
          <p className="text-slate-500 mb-8">{c.updated}</p>

          {c.sections.map(([heading, paras, bullets, after, special, note, tail]) => (
            <section key={heading} className="mb-7">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-3">{heading}</h2>
              {paras && tail !== 'PRIVACY' && paras.map((p) => <p key={p} className="text-slate-600 mb-2">{p}</p>)}
              {tail === 'PRIVACY' && (
                <p className="text-slate-600">
                  {paras[0]}
                  <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700 underline">{c.privacyLink}</Link>.
                </p>
              )}
              {bullets && (
                <ul className="list-disc pl-5 text-slate-600 space-y-1 mb-2">
                  {bullets.map((b) => <li key={b}>{b}</li>)}
                </ul>
              )}
              {after && after.map((p) => <p key={p} className="text-slate-600">{p}</p>)}
              {special === 'PRIZES' && (
                <div className="overflow-hidden rounded-lg border border-slate-200 mb-3">
                  <table className="w-full text-left text-sm sm:text-base">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr>
                        <th className="px-4 py-2 font-semibold">{c.place}</th>
                        <th className="px-4 py-2 font-semibold text-right">{c.prize}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PRIZES.map((p) => (
                        <tr key={p.en} className="border-t border-slate-100">
                          <td className="px-4 py-2 text-slate-600">{p[lang]}</td>
                          <td className="px-4 py-2 text-slate-800 font-medium text-right tabular-nums">{c.eur(p.eur)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {note && note.map((p) => <p key={p} className="text-slate-500 text-sm">{p}</p>)}
            </section>
          ))}
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          © 2026 SocraQuest
        </div>
      </footer>
    </div>
  );
}
